import { Spin } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    ConfirmFooterButton,
    InfoIconBadge,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalSubtitle,
    ModalTitle,
    ModalTitleBlock,
    OptionCard,
    OptionDescription,
    OptionRadio,
    OptionText,
    OptionTitle,
    OptionsList,
    StyledSaveOnLeaveModal,
} from './SaveOnLeavePrompt.styles';

export type SaveOnLeavePromptProps = {
    open: boolean;
    viewName?: string;
    canUpdateMainView: boolean;
    isSubmitting?: boolean;
    onCancel: () => void;
    onUpdateMainView: () => Promise<void>;
    onIgnore: () => Promise<void>;
    onDiscard: () => Promise<void>;
};

type LeaveAction = 'update-main-view' | 'save-scenario-only' | 'discard';

type LeaveOptionConfig = {
    value: LeaveAction;
    title: string;
    description: string;
    isDisabled?: boolean;
};

const SaveOnLeavePrompt = ({
    open,
    viewName = 'This view',
    canUpdateMainView,
    isSubmitting = false,
    onCancel,
    onUpdateMainView,
    onIgnore,
    onDiscard,
}: SaveOnLeavePromptProps) => {
    const [selectedAction, setSelectedAction] = useState<LeaveAction | null>(null);

    useEffect(() => {
        if (open) {
            setSelectedAction(null);
        }
    }, [open]);

    useEffect(() => {
        if (!canUpdateMainView && selectedAction === 'update-main-view') {
            setSelectedAction(null);
        }
    }, [canUpdateMainView, selectedAction]);

    const options = useMemo<LeaveOptionConfig[]>(
        () => [
            {
                value: 'update-main-view',
                title: 'Update',
                description: 'Updates the original view,\nchanges are saved to the scenario.',
                isDisabled: !canUpdateMainView,
            },
            {
                value: 'save-scenario-only',
                title: 'Cancel',
                description:
                    'View changes are discarded,\nchanges saved to the scenario only.',
            },
            {
                value: 'discard',
                title: 'Discard',
                description: 'Discards all changes,\nreverts scenario to original view.',
            },
        ],
        [canUpdateMainView],
    );

    const displayViewName = viewName.trim() || 'This view';
    const isConfirmEnabled = selectedAction != null && !isSubmitting;

    const handleCancel = useCallback(() => {
        if (isSubmitting) {
            return;
        }
        onCancel();
    }, [isSubmitting, onCancel]);

    const runAction = useCallback(async (action: () => Promise<void>) => {
        try {
            await action();
        } catch {
            // Parent handles errors; keep prompt open for retry.
        }
    }, []);

    const handleConfirm = useCallback(() => {
        if (!isConfirmEnabled || selectedAction == null) {
            return;
        }

        if (selectedAction === 'update-main-view') {
            void runAction(onUpdateMainView);
            return;
        }

        if (selectedAction === 'save-scenario-only') {
            void runAction(onIgnore);
            return;
        }

        void runAction(onDiscard);
    }, [isConfirmEnabled, onDiscard, onIgnore, onUpdateMainView, runAction, selectedAction]);

    return (
        <StyledSaveOnLeaveModal
            open={open}
            footer={null}
            closable={false}
            centered
            width={480}
            maskClosable={false}
            destroyOnClose
            onCancel={handleCancel}
        >
            <ModalContent>
                <ModalHeader>
                    <InfoIconBadge aria-hidden>i</InfoIconBadge>
                    <ModalTitleBlock>
                        <ModalTitle>Unsaved View</ModalTitle>
                        <ModalSubtitle>{displayViewName} has unsaved changes.</ModalSubtitle>
                        <ModalSubtitle>Do you want to update it?</ModalSubtitle>
                    </ModalTitleBlock>
                </ModalHeader>

                <OptionsList role="radiogroup" aria-label="How to save unsaved view changes">
                    {options.map((option) => {
                        const isSelected = selectedAction === option.value;
                        return (
                            <OptionCard
                                key={option.value}
                                type="button"
                                role="radio"
                                aria-checked={isSelected}
                                isSelected={isSelected}
                                isDisabled={option.isDisabled || isSubmitting}
                                disabled={option.isDisabled || isSubmitting}
                                onClick={() => {
                                    if (option.isDisabled || isSubmitting) {
                                        return;
                                    }
                                    setSelectedAction(option.value);
                                }}
                            >
                                <OptionRadio isSelected={isSelected} aria-hidden />
                                <OptionText>
                                    <OptionTitle>{option.title}</OptionTitle>
                                    <OptionDescription>{option.description}</OptionDescription>
                                </OptionText>
                            </OptionCard>
                        );
                    })}
                </OptionsList>

                <ModalFooter>
                    <ConfirmFooterButton
                        type="button"
                        isEnabled={isConfirmEnabled}
                        disabled={!isConfirmEnabled}
                        onClick={handleConfirm}
                    >
                        {isSubmitting ? <Spin size="small" /> : 'Confirm'}
                    </ConfirmFooterButton>
                </ModalFooter>
            </ModalContent>
        </StyledSaveOnLeaveModal>
    );
};

export default SaveOnLeavePrompt;
